// American Football pitch drawer.
//
// Coordinates emitted by the backend (see
// libs/core/.../sports/american_football/pitch.ts):
//   P1..P13 = bottom sideline, x=-60..+60 in 10m increments (y=0)
//   P14..P26 = top sideline, x=+60..-60 in 10m increments (y=68)
//   P27/P28 = left endline hashes at y=31.18 / 36.82
//   P29/P30 = right endline hashes at y=31.18 / 36.82
//
// Rendered features:
//   - Pitch fill background
//   - Outer boundary (P1 → P13 → P14 → P26 → P1)
//   - End zones on either end (x=[-60..-50] and x=[+50..+60]) with a
//     slightly different fill tint
//   - Yard lines every 10 yards (solid) with shorter hash-marks at the
//     two inner rows between them
//   - Mid-field line emphasised (50-yard line)
//   - Goalpost verticals at each end (drawn as short lines at the goal
//     line, extending into the endzone)
import type { PitchCoordinates, WorldToCanvas } from '../../types'
import type { PitchDrawOptions } from '../types'
import { PITCH_FILL, PITCH_LINE, PITCH_LINE_WIDTH } from '../../constants'
import { YARD_M, HASH_INSET_YD, HASH_LEN_YD } from './constants'

export function drawAmericanFootballPitch(
  ctx: CanvasRenderingContext2D,
  pitch: PitchCoordinates,
  w2c: WorldToCanvas,
  canvasWidth: number,
  canvasHeight: number,
  options?: PitchDrawOptions
) {
  const fillColor = options?.fill ?? PITCH_FILL
  const lineColor = options?.line ?? PITCH_LINE
  const lineW = options?.lineWidth ?? PITCH_LINE_WIDTH

  const p = (key: string) => {
    const pt = pitch[key]
    if (!pt) return null
    return { ...pt, c: w2c(pt.x, pt.y) }
  }

  // Background
  ctx.fillStyle = fillColor
  ctx.fillRect(0, 0, canvasWidth, canvasHeight)

  const p1 = p('P1') // bottom-left corner (x=-60)
  const p13 = p('P13') // bottom-right corner (x=+60)
  const p14 = p('P14') // top-right corner
  const p26 = p('P26') // top-left corner
  if (!p1 || !p13 || !p14 || !p26) return

  // End zones: left is x=[-60..-50] (P1..P2 + P26..P25), right is
  // x=[+50..+60] (P12..P13 + P14..P15). Slightly darker fill.
  const p2 = p('P2')
  const p12 = p('P12')
  const p15 = p('P15')
  const p25 = p('P25')
  if (p2 && p25) {
    ctx.fillStyle = 'rgba(255, 255, 255, 0.04)'
    ctx.beginPath()
    ctx.moveTo(p1.c.cx, p1.c.cy)
    ctx.lineTo(p2.c.cx, p2.c.cy)
    ctx.lineTo(p25.c.cx, p25.c.cy)
    ctx.lineTo(p26.c.cx, p26.c.cy)
    ctx.closePath()
    ctx.fill()
  }
  if (p12 && p15) {
    ctx.fillStyle = 'rgba(255, 255, 255, 0.04)'
    ctx.beginPath()
    ctx.moveTo(p12.c.cx, p12.c.cy)
    ctx.lineTo(p13.c.cx, p13.c.cy)
    ctx.lineTo(p14.c.cx, p14.c.cy)
    ctx.lineTo(p15.c.cx, p15.c.cy)
    ctx.closePath()
    ctx.fill()
  }

  ctx.strokeStyle = lineColor
  ctx.lineWidth = lineW
  ctx.lineJoin = 'miter'

  // Outer boundary
  ctx.beginPath()
  ctx.moveTo(p1.c.cx, p1.c.cy)
  ctx.lineTo(p13.c.cx, p13.c.cy)
  ctx.lineTo(p14.c.cx, p14.c.cy)
  ctx.lineTo(p26.c.cx, p26.c.cy)
  ctx.closePath()
  ctx.stroke()

  // Yard lines — connect each bottom P to its top mirror. Bottom run is
  // P2..P12; top mirror is P25..P15 (reversed, both skip the end-line
  // corners).
  const yardPairs: [string, string][] = [
    ['P2', 'P25'],
    ['P3', 'P24'],
    ['P4', 'P23'],
    ['P5', 'P22'],
    ['P6', 'P21'],
    ['P7', 'P20'], // 50-yard line (x=0)
    ['P8', 'P19'],
    ['P9', 'P18'],
    ['P10', 'P17'],
    ['P11', 'P16'],
    ['P12', 'P15']
  ]
  for (const [bottomKey, topKey] of yardPairs) {
    const b = p(bottomKey)
    const t = p(topKey)
    if (!b || !t) continue
    const is50 = bottomKey === 'P7'
    ctx.lineWidth = is50 ? lineW * 1.4 : lineW
    ctx.beginPath()
    ctx.moveTo(b.c.cx, b.c.cy)
    ctx.lineTo(t.c.cx, t.c.cy)
    ctx.stroke()
  }
  ctx.lineWidth = lineW

  // Hash marks — two rows (one 23.5 yards from each sideline), one tick
  // per yard between the yard lines. Rendered as short vertical dashes.
  const pitchYMin = p1.y
  const pitchYMax = p26.y
  const hashYLow = pitchYMin + HASH_INSET_YD * YARD_M
  const hashYHigh = pitchYMax - HASH_INSET_YD * YARD_M
  const hashHalf = (HASH_LEN_YD * YARD_M) / 2

  // Walk each yard between the two end-zone lines (x=-50..+50), place a
  // tick at the low hash row and the high hash row. Skip x positions
  // that coincide with the 10-yard major lines (already drawn).
  const xStart = (p2 ?? p1).x
  const xEnd = (p12 ?? p13).x
  const dir = xEnd > xStart ? 1 : -1
  const step = 1 * YARD_M * dir
  for (let x = xStart; dir > 0 ? x <= xEnd : x >= xEnd; x += step) {
    // Skip the major 10-yard increments (within half a yard tolerance).
    const rounded = Math.round(x)
    if (Math.abs(x - rounded) < 0.01 && rounded % 10 === 0) continue

    for (const hashY of [hashYLow, hashYHigh]) {
      const top = w2c(x, hashY - hashHalf)
      const bot = w2c(x, hashY + hashHalf)
      ctx.beginPath()
      ctx.moveTo(top.cx, top.cy)
      ctx.lineTo(bot.cx, bot.cy)
      ctx.stroke()
    }
  }

  // Goalposts — P27/P28 flank the left end line at the inner hash width,
  // P29/P30 flank the right end line. Drawn as a single short vertical
  // line between each pair with a thicker stroke.
  const goalposts: [string, string][] = [
    ['P27', 'P28'],
    ['P29', 'P30']
  ]
  ctx.lineWidth = lineW * 1.8
  for (const [a, b] of goalposts) {
    const pa = p(a)
    const pb = p(b)
    if (!pa || !pb) continue
    ctx.beginPath()
    ctx.moveTo(pa.c.cx, pa.c.cy)
    ctx.lineTo(pb.c.cx, pb.c.cy)
    ctx.stroke()
  }
  ctx.lineWidth = lineW
}
